import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Activity,
  Clock,
  BarChart3,
  MousePointer2,
  Eye,
  ChevronDown,
  ChevronRight,
  Navigation,
  Play,
  Circle,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Search,
  User,
  Zap,
  TrendingUp,
  Target,
  Filter,
  X
} from 'lucide-react';
import { apiClient } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { formatDateTimeWithWeekday, formatTimeWithSeconds } from '../../../utils/dateUtils';

interface UserAction {
  id: string;
  action_type: string;
  item_type?: string;
  item_id?: string;
  request_url?: string;
  metadata?: any;
  created_at: string;
  success?: boolean;
  response_time?: number;
  error_message?: string;
  error_type?: string;
}

interface SessionData {
  session_id: string;
  start_time: string;
  end_time: string;
  action_count: number;
  actions: UserAction[];
  datasets_created?: number;
  results_generated?: number;
  credits_used?: number;
}

interface UserInfo {
  email?: string;
  name?: string;
  user_id: string;
}

interface AnalyticsData {
  sessions: SessionData[];
  total_sessions: number;
  user_info?: UserInfo;
}

// Session summary component for better overview
const SessionSummaryCard: React.FC<{ session: SessionData }> = ({ session }) => {
  const activityTypes = useMemo(() => {
    const types = {
      clicks: 0,
      views: 0,
      navigations: 0,
      submits: 0,
      errors: 0,
    };
    session.actions.forEach((action) => {
      if (action.action_type === 'click') types.clicks++;
      else if (action.action_type === 'view' || action.action_type === 'page_view') types.views++;
      else if (action.action_type === 'navigate') types.navigations++;
      else if (action.action_type === 'submit') types.submits++;
      if (action.success === false) types.errors++;
    });
    return types;
  }, [session.actions]);

  const avgResponseTime = useMemo(() => {
    const times = session.actions.filter(a => a.response_time).map(a => a.response_time!);
    if (times.length === 0) return null;
    return Math.round(times.reduce((a, b) => a + b, 0) / times.length);
  }, [session.actions]);

  const errorCount = useMemo(() => {
    return session.actions.filter(a => a.success === false).length;
  }, [session.actions]);

  const formatDuration = (startTime: string, endTime: string) => {
    const start = new Date(startTime);
    const end = new Date(endTime);
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.round(diffMs / (1000 * 60));
    const diffSecs = Math.round(diffMs / 1000);

    if (diffMins === 0) {
      return diffSecs === 0 ? '<1s' : `${diffSecs}s`;
    }
    return diffMins === 1 ? '1 min' : `${diffMins} mins`;
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      {/* Duration */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
          <Clock className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <div className="text-xs text-gray-600">Duration</div>
          <div className="text-sm font-semibold text-gray-900">{formatDuration(session.start_time, session.end_time)}</div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
          <Activity className="w-4 h-4 text-purple-600" />
        </div>
        <div>
          <div className="text-xs text-gray-600">Actions</div>
          <div className="text-sm font-semibold text-gray-900">{session.action_count}</div>
        </div>
      </div>

      {/* Errors */}
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 ${errorCount > 0 ? 'bg-red-100' : 'bg-green-100'} rounded-lg flex items-center justify-center`}>
          {errorCount > 0 ? (
            <AlertCircle className="w-4 h-4 text-red-600" />
          ) : (
            <CheckCircle2 className="w-4 h-4 text-green-600" />
          )}
        </div>
        <div>
          <div className="text-xs text-gray-600">Errors</div>
          <div className={`text-sm font-semibold ${errorCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
            {errorCount > 0 ? errorCount : 'None'}
          </div>
        </div>
      </div>

      {/* Avg Response */}
      {avgResponseTime && (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
            <Zap className="w-4 h-4 text-orange-600" />
          </div>
          <div>
            <div className="text-xs text-gray-600">Avg Response</div>
            <div className="text-sm font-semibold text-gray-900">{avgResponseTime}ms</div>
          </div>
        </div>
      )}

      {/* Activity Breakdown - Spans all columns */}
      <div className="col-span-2 md:col-span-4 pt-2 mt-2 border-t border-gray-200 space-y-2">
        {/* Activity Types */}
        <div className="flex items-center gap-4 flex-wrap">
          {activityTypes.clicks > 0 && (
            <div className="flex items-center gap-1.5 text-xs">
              <MousePointer2 className="w-3.5 h-3.5 text-blue-600" />
              <span className="font-medium text-gray-700">{activityTypes.clicks} Clicks</span>
            </div>
          )}
          {activityTypes.views > 0 && (
            <div className="flex items-center gap-1.5 text-xs">
              <Eye className="w-3.5 h-3.5 text-green-600" />
              <span className="font-medium text-gray-700">{activityTypes.views} Views</span>
            </div>
          )}
          {activityTypes.navigations > 0 && (
            <div className="flex items-center gap-1.5 text-xs">
              <Navigation className="w-3.5 h-3.5 text-purple-600" />
              <span className="font-medium text-gray-700">{activityTypes.navigations} Navigations</span>
            </div>
          )}
          {activityTypes.errors > 0 && (
            <div className="flex items-center gap-1.5 text-xs">
              <AlertCircle className="w-3.5 h-3.5 text-red-600" />
              <span className="font-medium text-red-700">{activityTypes.errors} Errors</span>
            </div>
          )}
        </div>

        {/* Session Metrics (if available) */}
        {(session.datasets_created !== undefined || session.results_generated !== undefined || session.credits_used !== undefined) && (
          <div className="flex items-center gap-4 flex-wrap text-xs">
            {session.datasets_created !== undefined && session.datasets_created > 0 && (
              <div className="flex items-center gap-1.5">
                <BarChart3 className="w-3.5 h-3.5 text-purple-600" />
                <span className="font-medium text-gray-700">{session.datasets_created} Dataset{session.datasets_created !== 1 ? 's' : ''}</span>
              </div>
            )}
            {session.results_generated !== undefined && session.results_generated > 0 && (
              <div className="flex items-center gap-1.5">
                <Target className="w-3.5 h-3.5 text-blue-600" />
                <span className="font-medium text-gray-700">{session.results_generated} Result{session.results_generated !== 1 ? 's' : ''}</span>
              </div>
            )}
            {session.credits_used !== undefined && session.credits_used > 0 && (
              <div className="flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5 text-orange-600" />
                <span className="font-medium text-gray-700">{session.credits_used.toLocaleString()} Credit{session.credits_used !== 1 ? 's' : ''}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const UserAnalyticsTab: React.FC = () => {
  const { user } = useAuth();
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [expandedActions, setExpandedActions] = useState<Set<string>>(new Set());
  const [userIdInput, setUserIdInput] = useState<string>('');

  // Filter states
  const [dateFilter, setDateFilter] = useState<string>('all'); // all, 24h, 7d, 30d
  const [successFilter, setSuccessFilter] = useState<string>('all'); // all, success, failed
  const [actionTypeFilter, setActionTypeFilter] = useState<string>('all'); // all, click, view, navigate, submit
  const [showFilters, setShowFilters] = useState(false);

  const isMountedRef = useRef(false);

  const fetchAnalytics = useCallback(async (targetUserId?: string) => {
    if (!user?.id) {
      setError('User not found. Please log in again.');
      setLoading(false);
      return;
    }

    const userId = targetUserId || userIdInput.trim() || user.id;

    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.getUserAnalytics(3, userId);
      setAnalyticsData(data);
    } catch (err: unknown) {
      console.error('Error fetching analytics:', err);
      const error = err as { response?: { status?: number } };
      if (error.response?.status === 403) {
        setError('Access denied. Your email is not authorized to view analytics.');
      } else if (error.response?.status === 401) {
        setError('Authentication required. Please log in.');
      } else if (error.response?.status === 404) {
        setError('User not found. Please check the User ID and try again.');
      } else {
        setError('Failed to load analytics data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [user, userIdInput]);

  useEffect(() => {
    if (user?.id && !isMountedRef.current) {
      isMountedRef.current = true;
      fetchAnalytics(user.id);
    }
  }, [user?.id, fetchAnalytics]);

  const handleUserIdSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    fetchAnalytics(userIdInput.trim());
  };

  const isViewingSelf = () => {
    return !userIdInput.trim() || userIdInput.trim() === user?.id;
  };

  // Use centralized date utils with proper validation
  const formatDate = formatDateTimeWithWeekday;
  const formatTime = formatTimeWithSeconds;

  const getActionIcon = (actionType: string, success: boolean = true) => {
    const iconClass = "w-4 h-4";

    if (!success) {
      return <AlertCircle className={iconClass} />;
    }

    switch (actionType) {
      case 'click':
        return <MousePointer2 className={iconClass} />;
      case 'view':
      case 'page_view':
        return <Eye className={iconClass} />;
      case 'navigate':
        return <Navigation className={iconClass} />;
      case 'submit':
        return <CheckCircle2 className={iconClass} />;
      default:
        return <Circle className={iconClass} />;
    }
  };

  const getActionTypeColor = (actionType: string, success: boolean = true) => {
    if (!success) {
      return 'bg-red-500 text-white';
    }

    switch (actionType) {
      case 'click':
        return 'bg-blue-500 text-white';
      case 'view':
      case 'page_view':
        return 'bg-green-500 text-white';
      case 'navigate':
        return 'bg-purple-500 text-white';
      case 'submit':
        return 'bg-emerald-500 text-white';
      case 'error':
        return 'bg-red-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  const getActionDescription = (action: UserAction) => {
    const { action_type, item_type, item_id, metadata } = action;

    const getButtonText = () => {
      if (metadata?.text_content && metadata.text_content.trim()) {
        const text = metadata.text_content.trim();
        if (text.length <= 50 && !text.includes('px-') && !text.includes('bg-')) {
          return text;
        }
      }

      if (metadata?.aria_label) {
        return metadata.aria_label;
      }

      if (item_id && !item_id.includes('px-') && !item_id.includes('bg-')) {
        return item_id.replace('_', ' ');
      }

      return null;
    };

    switch (action_type) {
      case 'click': {
        if (metadata?.action === 'expand_cell') {
          return `Expanded ${metadata.column_name} cell in ${metadata.table_type} table`;
        }
        if (metadata?.action === 'open_column_enrichment_popup') {
          return `Opened enrichment options for ${metadata.column_name}`;
        }
        if (metadata?.action === 'navigate_to_datasets') {
          return `Clicked ${metadata.source?.replace('_', ' ')} button`;
        }

        const buttonText = getButtonText();
        if (buttonText) {
          return `Clicked "${buttonText}" button`;
        }

        if (item_type === 'button') {
          return `Clicked button`;
        }

        return `Clicked ${item_type || 'element'}`;
      }
      case 'view':
      case 'page_view': {
        const page = item_id?.replace('/', '') || 'page';
        return `Viewed ${page} page`;
      }
      case 'navigate':
        return `Navigated to ${item_id || 'new page'}`;

      case 'submit':
        return `Submitted ${item_type || 'form'}${item_id ? `: ${item_id}` : ''}`;

      default:
        return `${action_type.replace('_', ' ')} ${item_type || ''}`;
    }
  };

  const toggleActionDetails = (actionId: string) => {
    const newExpanded = new Set(expandedActions);
    if (newExpanded.has(actionId)) {
      newExpanded.delete(actionId);
    } else {
      newExpanded.add(actionId);
    }
    setExpandedActions(newExpanded);
  };

  const handleSessionClick = (sessionId: string) => {
    const newSelected = selectedSession === sessionId ? null : sessionId;
    setSelectedSession(newSelected);
  };

  // Filter sessions based on selected filters
  const filteredSessions = useMemo(() => {
    if (!analyticsData?.sessions) return [];

    return analyticsData.sessions.filter(session => {
      // Date filter
      if (dateFilter !== 'all') {
        const sessionDate = new Date(session.start_time);
        const now = new Date();
        const hoursDiff = (now.getTime() - sessionDate.getTime()) / (1000 * 60 * 60);

        if (dateFilter === '24h' && hoursDiff > 24) return false;
        if (dateFilter === '7d' && hoursDiff > 24 * 7) return false;
        if (dateFilter === '30d' && hoursDiff > 24 * 30) return false;
      }

      // Success/Failure filter
      if (successFilter !== 'all') {
        const hasErrors = session.actions.some(a => a.success === false);
        if (successFilter === 'failed' && !hasErrors) return false;
        if (successFilter === 'success' && hasErrors) return false;
      }

      // Action type filter
      // Fixed: Normalize 'view' to match both 'view' and 'page_view'
      if (actionTypeFilter !== 'all') {
        const normalizeActionType = (type: string) => {
          if (type === 'page_view') return 'view';
          return type;
        };

        const hasActionType = session.actions.some(a => normalizeActionType(a.action_type) === actionTypeFilter);
        if (!hasActionType) return false;
      }

      return true;
    }).map(session => {
      // If action type filter is active, also filter actions within the session
      // Fixed: Normalize action types and avoid double filtering
      if (actionTypeFilter !== 'all') {
        const normalizeActionType = (type: string) => {
          if (type === 'page_view') return 'view';
          return type;
        };

        const filteredActions = session.actions.filter(a => normalizeActionType(a.action_type) === actionTypeFilter);

        return {
          ...session,
          actions: filteredActions,
          action_count: filteredActions.length
        };
      }
      return session;
    });
  }, [analyticsData?.sessions, dateFilter, successFilter, actionTypeFilter]);

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (dateFilter !== 'all') count++;
    if (successFilter !== 'all') count++;
    if (actionTypeFilter !== 'all') count++;
    return count;
  }, [dateFilter, successFilter, actionTypeFilter]);

  const clearAllFilters = () => {
    setDateFilter('all');
    setSuccessFilter('all');
    setActionTypeFilter('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-blue-600 mx-auto mb-4 animate-spin" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Loading Analytics</h2>
          <p className="text-gray-600 text-sm">Fetching session data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="max-w-md text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
            <p className="text-gray-600 mb-6 text-sm">{error}</p>
            <button
              onClick={() => fetchAnalytics(userIdInput.trim())}
              className="px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BarChart3 className="w-7 h-7 text-blue-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Analytics</h1>
          <p className="text-sm text-gray-600">Understand user behavior and session patterns</p>
        </div>
      </div>

      {/* User ID Search */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <form onSubmit={handleUserIdSubmit} className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={userIdInput}
                  onChange={(e) => setUserIdInput(e.target.value)}
                  placeholder={`Enter User ID (default: ${user?.id?.slice(0, 8) || 'your ID'}...)`}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition-all"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
              <span>{loading ? 'Loading...' : 'Search'}</span>
            </button>
          </div>

          <div className="space-y-2">
            {/* User Info Display */}
            {analyticsData?.user_info && (
              <div className="flex items-center gap-4 pb-2 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-600">
                    {analyticsData.user_info.email && (
                      <span className="font-medium text-gray-900">{analyticsData.user_info.email}</span>
                    )}
                    {analyticsData.user_info.name && (
                      <span className="text-gray-600 ml-2">({analyticsData.user_info.name})</span>
                    )}
                    {!analyticsData.user_info.email && !analyticsData.user_info.name && (
                      <span className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs">{analyticsData.user_info.user_id}</span>
                    )}
                  </span>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between text-sm">
              <p className="text-gray-600">
                {isViewingSelf() ? (
                  <>Viewing your analytics • <span className="font-semibold text-gray-900">{analyticsData?.sessions.length || 0}</span> recent sessions • <span className="font-semibold text-gray-900">{analyticsData?.total_sessions || 0}</span> total</>
                ) : (
                  <>Viewing user • <span className="font-semibold text-gray-900">{analyticsData?.sessions.length || 0}</span> recent sessions</>
                )}
              </p>
              {!isViewingSelf() && (
                <button
                  onClick={() => {
                    setUserIdInput('');
                    fetchAnalytics(user?.id);
                  }}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                >
                  View My Analytics
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 flex items-center justify-between border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-gray-600" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
            {activeFiltersCount > 0 && (
              <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full">
                {activeFiltersCount} active
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {activeFiltersCount > 0 && (
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 transition-colors"
              >
                <X className="w-4 h-4" />
                Clear all
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors flex items-center gap-1"
            >
              {showFilters ? 'Hide' : 'Show'} filters
              <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Date Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="all">All time</option>
                <option value="24h">Last 24 hours</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
              </select>
            </div>

            {/* Success/Failure Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={successFilter}
                onChange={(e) => setSuccessFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="all">All sessions</option>
                <option value="success">Success only</option>
                <option value="failed">With errors</option>
              </select>
            </div>

            {/* Action Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Action Type</label>
              <select
                value={actionTypeFilter}
                onChange={(e) => setActionTypeFilter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              >
                <option value="all">All types</option>
                <option value="click">Clicks</option>
                <option value="view">Views</option>
                <option value="navigate">Navigation</option>
                <option value="submit">Submissions</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      {activeFiltersCount > 0 && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">{filteredSessions.length}</span> session{filteredSessions.length !== 1 ? 's' : ''} match your filters
            {analyticsData && <span className="text-blue-700"> (out of {analyticsData.sessions.length} total)</span>}
          </p>
        </div>
      )}

      {/* Sessions List */}
      <div className="space-y-4">
        {filteredSessions.map((session, sessionIndex) => (
          <div key={session.session_id} className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            {/* Session Header */}
            <div
              className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => handleSessionClick(session.session_id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex items-center">
                    {selectedSession === session.session_id ?
                      <ChevronDown className="w-5 h-5 text-blue-600" /> :
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    }
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center ml-3">
                      <Play className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Session #{sessionIndex + 1}
                    </h3>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {formatDate(session.start_time)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg">
                    <Clock className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold text-blue-900">
                      {(() => {
                        const start = new Date(session.start_time);
                        const end = new Date(session.end_time);
                        const diffMs = end.getTime() - start.getTime();
                        const diffMins = Math.round(diffMs / (1000 * 60));
                        const diffSecs = Math.round(diffMs / 1000);
                        if (diffMins === 0) return diffSecs === 0 ? '<1s' : `${diffSecs}s`;
                        return diffMins === 1 ? '1 min' : `${diffMins} mins`;
                      })()}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-lg">
                    <Activity className="w-4 h-4 text-purple-600" />
                    <span className="font-semibold text-purple-900">{session.action_count} actions</span>
                  </div>
                </div>
              </div>

              {/* Session Summary Cards - Always Visible */}
              <SessionSummaryCard session={session} />
            </div>

            {/* Session Journey - Timeline */}
            {selectedSession === session.session_id && (
              <div className="border-t border-gray-200 bg-gray-50 px-8 py-8">
                <div className="mb-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <Navigation className="w-5 h-5 text-blue-600" />
                    User Journey Timeline
                  </h4>
                  <p className="text-sm text-gray-600">Detailed step-by-step interactions during this session</p>
                </div>

                <div className="relative">
                  {session.actions.map((action, index) => (
                    <div key={action.id} className="relative flex items-start group mb-2">
                      {/* Timeline Line with Gradient */}
                      {index < session.actions.length - 1 && (
                        <div className="absolute left-[22px] top-12 w-1 h-20 bg-gradient-to-b from-blue-300 via-purple-300 to-blue-300 rounded-full opacity-40"></div>
                      )}

                      {/* Action Node - Larger and more prominent */}
                      <div className="relative z-10 flex-shrink-0">
                        <div
                          className={`w-11 h-11 rounded-xl flex items-center justify-center shadow-lg border-4 border-white ${getActionTypeColor(action.action_type, action.success !== false)}`}
                        >
                          {getActionIcon(action.action_type, action.success !== false)}
                        </div>
                        {/* Step number badge */}
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-bold shadow-md">
                          {index + 1}
                        </div>
                      </div>

                      {/* Action Content - Enhanced */}
                      <div className="ml-5 flex-1 min-w-0 pb-6">
                        <div
                          className="cursor-pointer group-hover:bg-white rounded-xl p-4 -m-4 transition-all shadow-sm group-hover:shadow-md border border-transparent group-hover:border-gray-200"
                          onClick={() => toggleActionDetails(action.id)}
                        >
                          {/* Main Action Info */}
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <h5 className="text-sm font-semibold text-gray-900 mb-1.5 leading-relaxed">
                                {getActionDescription(action)}
                              </h5>
                              <div className="flex items-center flex-wrap gap-2 text-xs">
                                <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded font-medium">
                                  {formatTime(action.created_at)}
                                </span>
                                {action.response_time && (
                                  <span className={`px-2 py-1 rounded font-medium ${
                                    action.response_time < 200 ? 'bg-green-100 text-green-700' :
                                    action.response_time < 500 ? 'bg-yellow-100 text-yellow-700' :
                                    'bg-orange-100 text-orange-700'
                                  }`}>
                                    {action.response_time}ms
                                  </span>
                                )}
                                {action.success === false && (
                                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Failed
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Expand Indicator */}
                            <div className="ml-4 flex-shrink-0">
                              {expandedActions.has(action.id) ?
                                <ChevronDown className="w-5 h-5 text-gray-400" /> :
                                <ChevronRight className="w-5 h-5 text-gray-400" />
                              }
                            </div>
                          </div>

                          {/* Expanded Details */}
                          {expandedActions.has(action.id) && (
                            <div className="mt-4 pt-4 border-t border-gray-200 bg-gray-50 rounded-lg p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                                <div>
                                  <div className="font-semibold text-gray-700 mb-2 flex items-center gap-1">
                                    <Navigation className="w-3.5 h-3.5" />
                                    Request URL
                                  </div>
                                  <div className="text-gray-600 break-all bg-white p-3 rounded border border-gray-200 text-xs font-mono shadow-sm">
                                    {action.request_url}
                                  </div>
                                </div>

                                <div>
                                  <div className="font-semibold text-gray-700 mb-2">Details</div>
                                  <div className="text-gray-600 space-y-1 bg-white p-3 rounded border border-gray-200 shadow-sm">
                                    <div>Type: <span className="font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-semibold">{action.action_type}</span></div>
                                    {action.item_type && (
                                      <div>Item: <span className="font-mono bg-purple-50 text-purple-700 px-2 py-0.5 rounded font-semibold">{action.item_type}</span></div>
                                    )}
                                    {action.item_id && (
                                      <div>ID: <span className="font-mono bg-green-50 text-green-700 px-2 py-0.5 rounded font-semibold">{action.item_id}</span></div>
                                    )}
                                    {action.success === false && (
                                      <div className="mt-2 pt-2 border-t border-red-200">
                                        <div className="flex items-start gap-2">
                                          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                          <div className="flex-1">
                                            <div className="font-semibold text-red-700 text-sm mb-1">Error Details</div>
                                            {action.error_type && (
                                              <div className="text-xs mb-1">Type: <span className="font-mono bg-red-50 text-red-700 px-2 py-0.5 rounded font-semibold">{action.error_type}</span></div>
                                            )}
                                            {action.error_message && (
                                              <div className="text-xs text-red-700 bg-red-50 p-2 rounded border border-red-200 mt-1">{action.error_message}</div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {action.metadata && Object.keys(action.metadata).length > 0 && (
                                <div className="mt-4">
                                  <div className="font-semibold text-gray-700 mb-2">Metadata</div>
                                  <div className="bg-white p-4 rounded border border-gray-200 text-xs font-mono shadow-sm">
                                    <pre className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                                      {JSON.stringify(action.metadata, null, 2)}
                                    </pre>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Empty State */}
        {filteredSessions.length === 0 && (
          <div className="text-center py-20 bg-gradient-to-br from-gray-50 to-white rounded-xl border-2 border-dashed border-gray-300">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Activity className="w-10 h-10 text-blue-600" />
            </div>
            {activeFiltersCount > 0 ? (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Sessions Match Your Filters</h3>
                <p className="text-gray-600 text-sm max-w-md mx-auto leading-relaxed mb-4">
                  Try adjusting your filters to see more sessions.
                </p>
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Clear All Filters
                </button>
              </>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Sessions Found</h3>
                <p className="text-gray-600 text-sm max-w-md mx-auto leading-relaxed">
                  Start using the application to see user journey analytics. Your interactions will be tracked and displayed here.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
